import React, { useEffect, useMemo, useState } from "react";
import FilterListIcon from "@mui/icons-material/FilterList";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import ReportLoader from "../../atoms/reportLoader/ReportLoader";
import AnalysesFilters from "../analysesFilters/AnalysesFilters";
import "./AnalysesListStep.css";
import { EMPTY_FILTERS, SORTS } from "../../../conts/historyConsts";
import CircularProgress from "@mui/material/CircularProgress";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

/* ------------------------------ formatters ------------------------------ */
const nf = (digits = 0) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const fmtInt = (v) => nf(0).format(Math.round(v ?? 0));

const fmtEUR = (v) => {
  if (v == null) return "—"; // cost not calculated for this analysis
  if (v >= 1e9) return `${nf(2).format(v / 1e9)} Md€`;
  if (v >= 1e6) return `${nf(1).format(v / 1e6)} M€`;
  if (v >= 1e3) return `${nf(0).format(v / 1e3)} k€`;
  return `${nf(0).format(v)} €`;
};

const fmtCO2 = (t) => {
  if (t == null) return "—";
  if (t >= 1e5) return `${nf(2).format(t / 1e6)} Mt`;
  if (t >= 1e3) return `${nf(1).format(t / 1e3)} kt`;
  return `${nf(0).format(t)} t`;
};

const fmtDate = (raw) => {
  if (!raw) return "—";
  const [y, m, d] = String(raw).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
};

const noop = () => {};

/* -------------------------------- main ----------------------------------- */
// onOpen / onPreview / onDelete receive the full item of the clicked row
// preview = { item, data: { url, filename }, isLoading, error } (owned by MapScreen)
const AnalysesListStep = ({
  analyses,
  onFetch,
  onOpen = noop,
  openingId,
  onPreview = noop,
  onDelete = noop,
  preview,
  onClosePreview = noop,
  viewedId,
  onCloseViewed = noop,
}) => {
  const [draft, setDraft] = useState(EMPTY_FILTERS); // what the inputs show
  const [applied, setApplied] = useState(EMPTY_FILTERS); // what was sent to the API
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState({ key: "fire_date", dir: "desc" });
  const [page, setPage] = useState(1);
  const [confirmId, setConfirmId] = useState(null); // row waiting for delete confirmation

  // default: load everything (no filters) when the tab opens
  useEffect(() => {
    onFetch({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading, error } = analyses;
  const activeCount = Object.values(applied).filter((v) => v !== "").length;

  const items = useMemo(() => {
    const list = [...(data?.items ?? [])];
    const m = sort.dir === "asc" ? 1 : -1;
    return list.sort((a, b) => {
      const x = a[sort.key];
      const y = b[sort.key];
      if (x == null && y == null) return 0;
      if (x == null) return 1; // empty values always last
      if (y == null) return -1;
      return (x > y ? 1 : x < y ? -1 : 0) * m;
    });
  }, [data, sort]);

  const applyFilters = (values) => {
    setApplied(values);
    setPage(1);
    onFetch({ ...values, page: 1 });
  };

  const resetFilters = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
    onFetch({});
  };

  const goToPage = (p) => {
    setPage(p);
    onFetch({ ...applied, page: p });
  };

  const toggleSort = (key) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "desc" },
    );

  /* ---------------------------- report preview ---------------------------- */
  // Replaces the table + filters while a report is open. All hooks are declared
  // above, so this early return is safe, and the component stays mounted:
  // filters, sort and page are preserved when the user comes back.
  if (preview?.item) {
    const { item, data: pdf, isLoading: pdfLoading, error: pdfError } = preview;
    return (
      <div className="historic-step alist alist-preview">
        <header className="alist-preview__header">
         
          <div className="alist-preview__title">
            <h2>Rapport</h2>
            <p>
              {item.reference} · {fmtDate(item.fire_date)}
            </p>
          </div>
          {pdf && (
            <button
              type="button"
              className="alist-btn"
              onClick={() => {
                const a = document.createElement("a");
                a.href = pdf.url;
                a.download = pdf.filename;
                a.click();
              }}
            >
              Exporter PDF
            </button>
          )}
        </header>
        {pdfLoading && (
          <ReportLoader mode="load" label={`Rapport ${item.reference}`} />
        )}

        {pdfError && (
          <div className="alist-empty" role="alert">
            <p>{pdfError}</p>
            <button
              type="button"
              className="alist-btn alist-btn--solid"
              onClick={() => onPreview(item)}
            >
              Réessayer
            </button>
          </div>
        )}

        {pdf && (
          <iframe
            className="alist-preview__frame"
            src={pdf.url}
            title={`Rapport ${item.reference}`}
          />
        )}
      </div>
    );
  }

  /* ------------------------------- states -------------------------------- */
  let body;
  if (isLoading && !data) {
    body = (
      <div className="alist-skeleton" aria-busy="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="alist-skel" />
        ))}
      </div>
    );
  } else if (error && !data) {
    body = (
      <div className="alist-empty">
        <p>{error}</p>
        <button
          type="button"
          className="alist-btn alist-btn--solid"
          onClick={() => onFetch({ ...applied, page })}
        >
          Réessayer
        </button>
      </div>
    );
  } else if (items.length === 0) {
    body = (
      <div className="alist-empty">
        <p>
          {activeCount
            ? "Aucune analyse ne correspond à ces filtres."
            : "Aucune analyse pour le moment."}
        </p>
        {activeCount > 0 && (
          <button type="button" className="alist-btn" onClick={resetFilters}>
            Réinitialiser les filtres
          </button>
        )}
      </div>
    );
  } else {
    body = (
      <div className={`alist-table-wrap ${isLoading ? "is-loading" : ""}`}>
        <table className="alist-table">
          <thead>
            <tr>
              <th>Référence</th>
              <th>Date</th>
              <th className="is-num">Bâtis</th>
              <th className="is-num">CO₂</th>
              <th className="is-num">Coût</th>
              <th className="is-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr
                key={it.analysis_id}
                className={it.has_report ? "has-report" : ""}
              >
                <td className="alist-ref">
                  <span className="alist-ref__name">{it.reference}</span>
                  <span
                    className={`alist-tag ${
                      it.has_report ? "is-report" : "is-impact"
                    }`}
                  >
                    {it.has_report
                      ? "Analyse complète"
                      : "Impacts environnementaux"}
                  </span>
                </td>
                <td>{fmtDate(it.fire_date)}</td>
                <td className="is-num">{fmtInt(it.buildings_saved)}</td>
                <td className="is-num">{fmtCO2(it.co2_saved_t)}</td>
                <td
                  className={`is-num ${
                    it.preserved_value_eur == null ? "is-muted" : "is-cost"
                  }`}
                  title={
                    it.preserved_value_eur == null
                      ? "Coût non calculé"
                      : undefined
                  }
                >
                  {fmtEUR(it.preserved_value_eur)}
                </td>
                <td className="is-actions">
                  {confirmId === it.analysis_id ? (
                    <span className="alist-confirm">
                      Supprimer ?
                      <button
                        type="button"
                        className="is-yes"
                        onClick={() => {
                          onDelete(it);
                          setConfirmId(null);
                        }}
                      >
                        Oui
                      </button>
                      <button type="button" onClick={() => setConfirmId(null)}>
                        Non
                      </button>
                    </span>
                  ) : (
                    <span className="alist-actions">
                      {(() => {
                        const isViewed = viewedId === it.analysis_id;
                        return (
                          <button
                            type="button"
                            className="alist-icon"
                            title={
                              isViewed
                                ? "Masquer l’analyse de la carte"
                                : "Afficher l’analyse sur la carte"
                            }
                            aria-label={
                              isViewed
                                ? `Fermer ${it.reference}`
                                : `Ouvrir ${it.reference}`
                            }
                            disabled={openingId === it.analysis_id}
                            onClick={() =>
                              isViewed ? onCloseViewed() : onOpen(it)
                            }
                          >
                            {openingId === it.analysis_id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : isViewed ? (
                              <VisibilityIcon fontSize="small" />
                            ) : (
                              <VisibilityOffIcon fontSize="small" />
                            )}
                          </button>
                        );
                      })()}
                      <button
                        type="button"
                        className="alist-icon alist-icon--pdf"
                        title={
                          it.has_report
                            ? "Aperçu du rapport PDF"
                            : "Aucun rapport généré"
                        }
                        aria-label={`Aperçu du rapport de ${it.reference}`}
                        disabled={!it.has_report}
                        onClick={() => onPreview(it)}
                      >
                        <PictureAsPdfOutlinedIcon fontSize="small" />
                      </button>
                      <button
                        type="button"
                        className="alist-icon alist-icon--danger"
                        title="Supprimer"
                        aria-label={`Supprimer ${it.reference}`}
                        onClick={() => setConfirmId(it.analysis_id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="historic-step alist">
      {/* sort chips + filters toggle ------------------------------------------ */}
      <div className="alist-toolbar">
        <div className="alist-chips" role="group" aria-label="Trier par">
          {SORTS.map((s) => {
            const on = sort.key === s.id;
            return (
              <button
                key={s.id}
                type="button"
                className={`alist-chip ${on ? "is-active" : ""}`}
                aria-pressed={on}
                onClick={() => toggleSort(s.id)}
              >
                {s.label}
                {on && (
                  <span aria-hidden="true">
                    {sort.dir === "asc" ? " ↑" : " ↓"}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className={`alist-filterbtn ${filtersOpen ? "is-open" : ""}`}
          onClick={() => setFiltersOpen((o) => !o)}
          aria-expanded={filtersOpen}
        >
          <FilterListIcon fontSize="small" />
          Filtres
          {activeCount > 0 && (
            <span className="alist-badge">{activeCount}</span>
          )}
        </button>
      </div>

      {filtersOpen && (
        <AnalysesFilters
          value={draft}
          onChange={setDraft}
          onSubmit={applyFilters}
          onReset={resetFilters}
        />
      )}
      {body}

      {/* pagination (only when the API returns several pages) ------------------ */}
      {data?.pages > 1 && (
        <div className="alist-pager">
          <button
            type="button"
            className="alist-btn"
            disabled={page <= 1 || isLoading}
            onClick={() => goToPage(page - 1)}
          >
            Précédent
          </button>
          <span>
            Page {page} / {data.pages}
          </span>
          <button
            type="button"
            className="alist-btn"
            disabled={page >= data.pages || isLoading}
            onClick={() => goToPage(page + 1)}
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  );
};

export default AnalysesListStep;
